"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applyAggregate = exports.CRUD_AGGREGATE_TOTAL_COLUMN = void 0;
const filter_sql_1 = require("./filter-sql");
const aggregate_types_1 = require("./aggregate-types");
/** Имя служебной колонки «всего групп» в строках результата. */
exports.CRUD_AGGREGATE_TOTAL_COLUMN = '__total';
const TABLE_NAME_RE = /^[a-z_][a-z0-9_.]+$/;
const SCOPE_COLUMN_RE = /^[a-z_][a-z0-9_]*$/;
/** date_bin-интервалы для sub-day бакетов. `1d` идёт через date_trunc (tz-aware). */
const TIME_BUCKET_INTERVAL_SQL = {
    '1m': '1 minute',
    '5m': '5 minutes',
    '10m': '10 minutes',
    '15m': '15 minutes',
    '30m': '30 minutes',
    '1h': '1 hour',
};
/**
 * Origin для date_bin — понедельник 2000-01-03 00:00 UTC. Для sub-day
 * интервалов выбор origin влияет только на фазу сетки; полночь понедельника
 * выравнивает и минутные, и часовые бакеты естественным образом.
 */
const DATE_BIN_ORIGIN = "TIMESTAMPTZ '2000-01-03 00:00:00+00'";
const applyAggregate = (q, opts) => {
    if (!TABLE_NAME_RE.test(opts.tableName)) {
        throw new Error(`applyAggregate: unsafe tableName "${opts.tableName}"`);
    }
    const maxGroups = opts.maxGroups ?? aggregate_types_1.DEFAULT_CRUD_AGGREGATE_LIMITS.maxGroups;
    const params = [];
    const addParam = (value) => {
        params.push(value);
        return `$${params.length}`;
    };
    const mapField = (field) => {
        if (!opts.allowedFields.includes(field)) {
            throw new Error(`applyAggregate: field '${field}' is not in allowedFields whitelist (zod should have rejected earlier)`);
        }
        return opts.fieldMapping?.[field] ?? field;
    };
    const assertAlias = (alias) => {
        if (!aggregate_types_1.CRUD_AGGREGATE_ALIAS_RE.test(alias)) {
            throw new Error(`applyAggregate: unsafe alias '${alias}' (zod should have rejected earlier)`);
        }
        return alias;
    };
    /** Под-фильтр (`filter` запроса / `where` агрегата) с продолжением нумерации $N. */
    const subFilter = (ast) => {
        const res = (0, filter_sql_1.applyFilter)(ast, {
            allowedFields: opts.allowedFields,
            fieldMapping: opts.fieldMapping,
            paramOffset: params.length,
            likeMode: opts.likeMode,
            arrayFields: opts.arrayFields,
        });
        params.push(...res.params);
        return res.sql;
    };
    // ── groupBy SELECT-выражения ──
    const groupByExprSql = (expr) => {
        if ('field' in expr) {
            return mapField(expr.field);
        }
        if ('timeBucket' in expr) {
            const col = mapField(expr.timeBucket.field);
            const interval = expr.timeBucket.interval;
            if (interval === '1d') {
                // Локальная полночь в tz, результат — timestamptz (UTC на wire).
                const tz1 = addParam(expr.timeBucket.tz ?? 'UTC');
                const tz2 = addParam(expr.timeBucket.tz ?? 'UTC');
                return `(date_trunc('day', ${col} AT TIME ZONE ${tz1}) AT TIME ZONE ${tz2})`;
            }
            return `date_bin(${addParam(TIME_BUCKET_INTERVAL_SQL[interval])}::interval, ${col}, ${DATE_BIN_ORIGIN})`;
        }
        // extract
        const col = mapField(expr.extract.field);
        const tz = addParam(expr.extract.tz ?? 'UTC');
        switch (expr.extract.part) {
            case 'hour':
                return `EXTRACT(HOUR FROM ${col} AT TIME ZONE ${tz})::int`;
            case 'dow':
                return `EXTRACT(ISODOW FROM ${col} AT TIME ZONE ${tz})::int`;
            case 'date':
                return `to_char(${col} AT TIME ZONE ${tz}, 'YYYY-MM-DD')`;
        }
    };
    // ── aggr SELECT-выражения ──
    const aggregateExprSql = (expr) => {
        const col = expr.field !== undefined ? mapField(expr.field) : null;
        const core = (() => {
            switch (expr.fn) {
                case 'count':
                    return col === null ? 'COUNT(*)' : `COUNT(${col})`;
                case 'count_distinct':
                    if (col === null)
                        throw new Error("applyAggregate: count_distinct requires 'field'");
                    return `COUNT(DISTINCT ${col})`;
                case 'sum':
                case 'avg':
                case 'min':
                case 'max':
                    if (col === null)
                        throw new Error(`applyAggregate: ${expr.fn} requires 'field'`);
                    return `${expr.fn.toUpperCase()}(${col})`;
            }
        })();
        const filtered = expr.where !== undefined ? `${core} FILTER (WHERE ${subFilter(expr.where)})` : core;
        // count/sum/avg без каста pg вернёт bigint/numeric СТРОКОЙ — приводим к
        // float8 (аналитика, точность достаточна). min/max сохраняют тип колонки.
        const needsCast = expr.fn !== 'min' && expr.fn !== 'max';
        return needsCast ? `(${filtered})::float8` : filtered;
    };
    // Порядок построения фиксирует нумерацию $N: SELECT-выражения (groupBy →
    // aggr) → WHERE → HAVING → LIMIT/OFFSET.
    const groupEntries = Object.entries(q.groupBy ?? {});
    const groupSelect = groupEntries.map(([alias, expr]) => `${groupByExprSql(expr)} AS "${assertAlias(alias)}"`);
    const aggrEntries = Object.entries(q.aggr);
    if (aggrEntries.length === 0) {
        throw new Error('applyAggregate: aggr must contain at least one aggregate');
    }
    const aliasToAggrSql = {};
    const aggrSelect = aggrEntries.map(([alias, expr]) => {
        const sql = aggregateExprSql(expr);
        aliasToAggrSql[assertAlias(alias)] = sql;
        return `${sql} AS "${alias}"`;
    });
    const filterSql = q.filter !== undefined ? subFilter(q.filter) : '';
    const scopeSql = (() => {
        if (opts.scope === undefined)
            return '';
        if (!SCOPE_COLUMN_RE.test(opts.scope.column)) {
            throw new Error(`applyAggregate: unsafe scope column "${opts.scope.column}"`);
        }
        return `${opts.scope.column} = ${addParam(opts.scope.value)}`;
    })();
    const whereSql = [filterSql, scopeSql].filter((part) => part !== '' && part !== 'TRUE').join(' AND ');
    // HAVING компилируется тем же applyFilter: «поля» — алиасы агрегатов,
    // fieldMapping подставляет ПОЛНОЕ агрегатное выражение (на алиас SELECT'а
    // в HAVING ссылаться нельзя). $N внутри выражений уже зафиксированы.
    const havingSql = (() => {
        if (q.having === undefined)
            return '';
        const res = (0, filter_sql_1.applyFilter)(q.having, {
            allowedFields: Object.keys(aliasToAggrSql),
            fieldMapping: aliasToAggrSql,
            paramOffset: params.length,
        });
        params.push(...res.params);
        return res.sql;
    })();
    // ── Сборка ──
    const clauses = [
        `SELECT ${[...groupSelect, ...aggrSelect].join(', ')}`,
        `FROM ${opts.tableName}`,
    ];
    if (whereSql !== '' && whereSql !== 'TRUE')
        clauses.push(`WHERE ${whereSql}`);
    if (groupEntries.length > 0) {
        clauses.push(`GROUP BY ${groupEntries.map((_, i) => i + 1).join(', ')}`);
    }
    if (havingSql !== '' && havingSql !== 'TRUE')
        clauses.push(`HAVING ${havingSql}`);
    const innerSql = clauses.join(' ');
    // Дефолтная сортировка — по groupBy-алиасам (детерминированная пагинация);
    // явный orderBy валидирован zod'ом (алиас ∈ groupBy ∪ aggr).
    const orderEntries = q.orderBy ?? groupEntries.map(([alias]) => ({ field: alias, dir: 'asc' }));
    const orderSql = orderEntries.length > 0
        ? ` ORDER BY ${orderEntries
            .map((e) => `"${assertAlias(e.field)}" ${e.dir === 'asc' ? 'ASC' : 'DESC'}`)
            .join(', ')}`
        : '';
    const limit = Math.min(q.limit ?? maxGroups, maxGroups);
    const offset = q.offset ?? 0;
    // Внешний SELECT: оконный COUNT(*) OVER() = всего групп (до limit/offset) —
    // один roundtrip вместо отдельного count-запроса.
    const sql = `SELECT sub.*, (COUNT(*) OVER())::int AS "${exports.CRUD_AGGREGATE_TOTAL_COLUMN}" ` +
        `FROM (${innerSql}) AS sub${orderSql} ` +
        `LIMIT ${addParam(limit)} OFFSET ${addParam(offset)}`;
    return { sql, params };
};
exports.applyAggregate = applyAggregate;
//# sourceMappingURL=aggregate-sql.js.map